import type { AnalysisOptionsDTO } from '@/api/assessments/api';

/** Bucket a knowledge card belongs to in the transcript analysis step. */
export type AssetBucket = 'transcript' | 'client-doc';

/** Step 2 analysis options controlling LLM focus areas for transcript analysis. */
export interface AnalysisOptions {
  targetOpportunitySignals: string;
  maturityFramework: string;
  additionalAnalysisContext: string;
}

/** Default empty analysis options used when no options have been configured yet. */
export const EMPTY_ANALYSIS_OPTIONS: AnalysisOptions = {
  targetOpportunitySignals: '',
  maturityFramework: '',
  additionalAnalysisContext: '',
};

/**
 * Convert a backend AnalysisOptionsDTO to the frontend AnalysisOptions shape.
 *
 * @param dto - The DTO from the API response, which may be null or undefined.
 * @returns Frontend AnalysisOptions with empty strings as defaults.
 */
export const analysisOptionsFromDTO = (
  dto: AnalysisOptionsDTO | null | undefined,
): AnalysisOptions => ({
  targetOpportunitySignals: dto?.target_opportunity_signals ?? '',
  maturityFramework: dto?.maturity_framework ?? '',
  additionalAnalysisContext: dto?.additional_analysis_context ?? '',
});

/**
 * Convert frontend AnalysisOptions to the API DTO format.
 * Empty strings are sent as undefined so the backend treats them as unset.
 *
 * @param opts - The frontend analysis options to serialise.
 * @returns AnalysisOptionsDTO ready for the PATCH payload.
 */
export const analysisOptionsToDTO = (opts: AnalysisOptions): AnalysisOptionsDTO => ({
  target_opportunity_signals: opts.targetOpportunitySignals || undefined,
  maturity_framework: opts.maturityFramework || undefined,
  additional_analysis_context: opts.additionalAnalysisContext || undefined,
});

/** A minimal representation of a Step 1 supporting document card used in the Step 2 intake display. */
export interface Step1DocCard {
  cardId: string;
  title: string;
}
